import { useState } from 'react'
import type { Payment } from '@1130-studio/shared'
import {
  useCreatePayment,
  useDeletePayment,
  useMarkPaymentPaid,
  useMarkPaymentPending,
} from '../../api/payments'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { cn, formatDate } from '../../lib/utils'

interface PaymentsSectionProps {
  projectId: string
  payments: Payment[]
}

function formatAmount(amount: string): string {
  const num = parseFloat(amount)
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num)
}

export function PaymentsSection({ projectId, payments }: PaymentsSectionProps) {
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    type: 'milestone' as 'prepayment' | 'milestone' | 'final',
    dueDate: '',
  })

  const createPayment = useCreatePayment()
  const deletePayment = useDeletePayment()
  const markPaid = useMarkPaymentPaid()
  const markPending = useMarkPaymentPending()

  // Calculate totals
  const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount), 0)
  const paidAmount = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0)
  const paidPercent = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    await createPayment.mutateAsync({
      projectId,
      name: formData.name,
      amount: formData.amount,
      type: formData.type,
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
    })

    setShowForm(false)
    setFormData({ name: '', amount: '', type: 'milestone', dueDate: '' })
  }

  const handleTogglePaid = (payment: Payment) => {
    if (payment.status === 'paid') {
      markPending.mutate({ id: payment.id, projectId })
    } else {
      markPaid.mutate({ id: payment.id, projectId })
    }
  }

  const handleDelete = (paymentId: string) => {
    if (confirm('Удалить платёж?')) {
      deletePayment.mutate({ id: paymentId, projectId })
    }
  }

  return (
    <div className="space-y-6">
      {/* Progress Summary */}
      <div className="bg-bg-tertiary rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-text-secondary">Оплачено</span>
          <span className="text-sm font-mono font-semibold text-text-primary">
            {formatAmount(paidAmount.toString())} / {formatAmount(totalAmount.toString())}
          </span>
        </div>
        <div className="h-2 bg-bg-card rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-accent-lime to-accent-emerald rounded-full transition-all duration-500"
            style={{ width: `${paidPercent}%` }}
          />
        </div>
        <div className="mt-2 text-right">
          <span className="text-xs text-text-tertiary">{paidPercent}%</span>
        </div>
      </div>

      {/* Payments List */}
      <div className="space-y-3">
        {payments.map((payment) => (
          <div
            key={payment.id}
            className={cn(
              'flex items-center gap-4 p-4 bg-bg-card border border-border-subtle rounded-xl transition-all',
              payment.status === 'paid' && 'opacity-60'
            )}
          >
            {/* Checkbox */}
            <button
              onClick={() => handleTogglePaid(payment)}
              className={cn(
                'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all',
                payment.status === 'paid'
                  ? 'bg-accent-lime border-accent-lime'
                  : 'border-border-medium hover:border-accent-lime'
              )}
              disabled={markPaid.isPending || markPending.isPending}
            >
              {payment.status === 'paid' && (
                <svg className="w-4 h-4 text-bg-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>

            {/* Payment Info */}
            <div className="flex-1 min-w-0">
              <div className={cn('font-medium flex items-center gap-2', payment.status === 'paid' && 'line-through text-text-tertiary')}>
                {payment.name}
                {payment.type && (
                  <span className={cn(
                    'text-[11px] px-1.5 py-0.5 rounded border',
                    payment.type === 'prepayment' && 'bg-accent-blue/10 text-accent-blue border-accent-blue/20',
                    payment.type === 'milestone' && 'bg-accent-violet/10 text-accent-violet border-accent-violet/20',
                    payment.type === 'final' && 'bg-accent-lime/10 text-accent-lime border-accent-lime/20',
                  )}>
                    {payment.type === 'prepayment' ? 'Предоплата' : payment.type === 'milestone' ? 'Этап' : 'Финальная'}
                  </span>
                )}
              </div>
              {payment.dueDate && (
                <div className="text-xs text-text-tertiary mt-0.5">
                  {payment.status === 'paid' ? (
                    <>Оплачено {payment.paidAt && formatDate(payment.paidAt)}</>
                  ) : (
                    <>Срок: {formatDate(payment.dueDate)}</>
                  )}
                </div>
              )}
            </div>

            {/* Amount */}
            <div className={cn(
              'font-mono font-semibold',
              payment.status === 'paid' ? 'text-text-tertiary' : 'text-accent-lime'
            )}>
              {formatAmount(payment.amount)}
            </div>

            {/* Delete */}
            <button
              onClick={() => handleDelete(payment.id)}
              className="w-8 h-8 rounded-md text-text-tertiary hover:text-red-500 hover:bg-red-500/10 transition-all flex items-center justify-center"
              disabled={deletePayment.isPending}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        ))}

        {payments.length === 0 && !showForm && (
          <div className="text-center py-8 text-text-tertiary">
            <p>Нет платежей</p>
          </div>
        )}
      </div>

      {/* Add Payment Form */}
      {showForm ? (
        <form onSubmit={handleSubmit} className="bg-bg-card border border-border-subtle rounded-xl p-4 space-y-4">
          <Input
            label="Название"
            value={formData.name}
            onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
            placeholder="Предоплата 50%"
            required
          />

          <div>
            <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1.5">Тип</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'prepayment' | 'milestone' | 'final' })}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-blue/50"
            >
              <option value="prepayment">Предоплата</option>
              <option value="milestone">Этап</option>
              <option value="final">Финальная оплата</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Сумма (₽)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData((p) => ({ ...p, amount: e.target.value }))}
              placeholder="250000"
              required
            />

            <Input
              label="Срок оплаты"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData((p) => ({ ...p, dueDate: e.target.value }))}
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => setShowForm(false)}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={createPayment.isPending || !formData.name || !formData.amount}
            >
              {createPayment.isPending ? 'Добавление...' : 'Добавить'}
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="secondary"
          className="w-full border-dashed"
          onClick={() => setShowForm(true)}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Добавить платёж
        </Button>
      )}
    </div>
  )
}
